import React, { FC, useEffect, useMemo, useState } from 'react';

import { FilterLayout } from '../FilterLayout/index';
import { ClearButton } from '../ClearButton/index';
import { TRangeValues, TCurrentFilterRangeValue, TCurrentFilter } from '../../../../commonTypes/filters';
import { getCurrentFilterValueByName } from '../../utils';
import FnNumberFormat from '../../../../components/elements/FnInput/FnNumberFormat';
import { SpanName } from './styles';
import clsx from 'clsx';

interface Props {
  name: string;
  values: TRangeValues;
  currentFilterItems: TCurrentFilter[];
  onSetFilter: (data: TCurrentFilter) => void;
  onClearFilter: (data: string) => void;

  fixed?: number;
  prefix?: string;
  isQuick?: boolean;
  label?: string;
}
interface Range {
  max: number;
  min: number;
}
const defaultErrorState = { min: false, max: false };

export const NumericRange: FC<Props> = ({
  name,
  values,
  currentFilterItems,
  onSetFilter,
  onClearFilter,
  fixed = 0,
  prefix = '',
  isQuick = false,
  label = ''
}: Props) => {
  const defaultValue: Range = useMemo(() => ({ min: values[0].value, max: values[1].value }), [values]);
  const [value, setValue] = useState<Range>(defaultValue);
  const [error, setError] = useState<{ min: boolean; max: boolean }>(defaultErrorState);

  useEffect(() => {
    const currentValue = getCurrentFilterValueByName(currentFilterItems, name) as TCurrentFilterRangeValue;
    if (currentValue) {
      setValue({ min: currentValue[0], max: currentValue[1] });
    } else {
      setValue(defaultValue);
    }
  }, [currentFilterItems]);

  const handleClear = () => {
    onClearFilter(name);
  };
  const setFilterData = () => {
    if (value && (value.min !== defaultValue.min || value.max !== defaultValue.max)) {
      const filterItem = {
        internalName: name,
        values: [value.min, value.max] as TCurrentFilterRangeValue
      };
      onSetFilter(filterItem);
    } else {
      onClearFilter(name);
    }
  };
  const setMinFilterData = () => {
    if (value.min > value.max || value.min === undefined) {
      setError({ ...error, min: true });
      return;
    }
    setFilterData();
  };
  const setMaxFilterData = () => {
    if (value.max < value.min || value.max === undefined) {
      setError({ ...error, max: true });
      return;
    }
    setFilterData();
  };
  const handleChangeMinValue = (minValue) => {
    setValue({ ...value, min: minValue });
    setError(defaultErrorState);
  };
  const handleChangeMaxValue = (maxValue) => {
    setValue({ ...value, max: maxValue });
    setError(defaultErrorState);
  };

  return (
    <>
      <FilterLayout>
        <FnNumberFormat
          className={clsx({ ['is-error']: error.min }, 'filter-number-format')}
          defaultValue={value.min}
          value={value.min}
          title="From"
          onChange={handleChangeMinValue}
          maxValue={defaultValue.max}
          minValue={defaultValue.min}
          onBlur={setMinFilterData}
          decimalScale={fixed}
          prefix={prefix}
          error={!!error.min}
        />
        <FnNumberFormat
          className={clsx({ ['is-error']: error.max }, 'filter-number-format')}
          defaultValue={value.max}
          value={value.max}
          title="To"
          onChange={handleChangeMaxValue}
          maxValue={defaultValue.max}
          minValue={defaultValue.min}
          onBlur={setMaxFilterData}
          prefix={prefix}
          error={!!error.max}
        />
      </FilterLayout>
      {!isQuick && <ClearButton onClear={handleClear} />}
    </>
  );
};
